import React, {
  Dispatch, SetStateAction, useEffect, useState,
} from 'react';
import {
  View, Text, StyleSheet, ActivityIndicator, Pressable,
} from 'react-native';
import Modal from 'react-native-modal';
import { useDispatch, useSelector } from 'react-redux';
import Style from '../../../../constants/Style';
import Colors from '../../../../constants/Colors';
import Button from '../../../../components/Button';
import PressableStyled from '../../../../components/PressableStyled';
import PlusIcon from '../../../../components/16pxIcons/PlusIcon';
import { AssetInstance, Template } from '../../../../constants/Types';
import { AssetSortCategory } from '../../../../requests/Assets/GetAssets';
import AssetFilterItem, {
  DropdownSelection, AssetFilterCategory, AssetFilterSelection,
} from './components/AssetFilterItem';
import isMobile from '../../../../components/IsMobile';
import SelectionModal from '../../../../components/SelectionModal';
import PlusIcon16 from '../../../../components/16pxIcons/PlusIcon16';
import AssetSortSelection from './components/AssetSortSelection';
import CheckBoxItem from '../../../../components/CheckBoxItem';
import {
  resetAllAssetFilters, setAssetsFilterSaved, updateAssetsFilter,
} from '../../../../redux/assetsFilterSlice';
import { Area, Floor, Space } from '../../../../components/Map/SetFloorsModal/FloorItem';

interface AssetFilterModalProps {
  assetFilterShown: boolean,
  hide: () => void,
  assetFilterResults: AssetInstance[],
  templates: Template[],
  selectedSortCategory: AssetSortCategory,
  setSelectedSortCategory: Dispatch<SetStateAction<AssetSortCategory>>,
  sortDescendingSelected: boolean,
  setSortDescendingSelected: Dispatch<SetStateAction<boolean>>,
  selectedFilters: AssetFilterSelection[],
  setSelectedFilters: Dispatch<SetStateAction<AssetFilterSelection[]>>,
  apply: () => void,
  loading: boolean,
  selectedFloor: Floor,
  setSelectedFloor: Dispatch<SetStateAction<Floor | null>>,
  selectedArea: Area,
  setSelectedArea: Dispatch<SetStateAction<Area | null>>,
  selectedSpace: Space,
  setSelectedSpace: Dispatch<SetStateAction<Space | null>>,
  appliedFilters: AssetFilterSelection[],
  resetAssetFilter: () => void,
  resetOnHide: () => void,
}

const AssetFilterModal = ({
  assetFilterShown, hide, assetFilterResults, templates, loading, selectedSortCategory,
  setSelectedSortCategory, sortDescendingSelected, setSortDescendingSelected, selectedFilters,
  setSelectedFilters, apply, selectedFloor, setSelectedFloor, selectedArea, setSelectedArea,
  selectedSpace, setSelectedSpace, appliedFilters, resetAssetFilter, resetOnHide,
}: AssetFilterModalProps) => {
  const dispatch = useDispatch();
  const filterSelectionSaved: boolean = useSelector((state:
    { assetsFilter: { saved: boolean } }) => state.assetsFilter.saved);
  const [backdropPressToHideEnabled, setBackdropPressToHideEnabled] = useState<boolean>(false);
  const [visibleFilterIndex, setVisibleFilterIndex] = useState<number | null>(null);
  const [dropdownSelection,
    setDropdownSelection] = useState<DropdownSelection>(DropdownSelection.CATEGORY);
  const [nameSuggestions] = useState<string[]>([]);
  const [idSuggestions] = useState<string[]>([]);
  const [modelSuggestions] = useState<string[]>([]);
  const [manufacturerSuggestions] = useState<string[]>([]);
  const [serialNumberSuggestions] = useState<string[]>([]);
  const [descriptionSuggestions] = useState<string[]>([]);
  const [floorDropdownShown, setFloorDropdownShown] = useState<boolean>(false);
  const [areaDropdownShown, setAreaDropdownShown] = useState<boolean>(false);
  const [spaceDropdownShown, setSpaceDropdownShown] = useState<boolean>(false);
  const [sortCategoryDropdownToggled, setSortCategoryDropdownToggled] = useState<boolean>(false);
  const [sortOrderDropdownToggled, setSortOrderDropdownToggled] = useState<boolean>(false);
  const addFilterButtonDisabled: boolean = selectedFilters
    .filter((filter: AssetFilterSelection) => !filter.category).length > 0
    || selectedFilters.length >= Object.values(AssetFilterCategory).length;
  const selectedLocationId: string | null = selectedSpace?.LocationId ?? selectedArea?.LocationId
    ?? selectedFloor?.LocationId ?? null;

  const categorySuggestions = (category: AssetFilterCategory) => {
    if (category === AssetFilterCategory.NAME) return nameSuggestions;
    if (category === AssetFilterCategory.ID) return idSuggestions;
    if (category === AssetFilterCategory.TEMPLATE) {
      return templates.sort((prev: Template, next: Template) => prev.name.localeCompare(next.name));
    }
    if (category === AssetFilterCategory.MODEL) return modelSuggestions;
    if (category === AssetFilterCategory.MANUFACTURER) return manufacturerSuggestions;
    if (category === AssetFilterCategory.SERIAL_NUMBER) return serialNumberSuggestions;
    if (category === AssetFilterCategory.DESCRIPTION) return descriptionSuggestions;
    return [];
  };

  const closeFilterDropdowns: () => void = () => {
    setVisibleFilterIndex(null);
    setFloorDropdownShown(false);
    setAreaDropdownShown(false);
    setSpaceDropdownShown(false);
  };

  const closeDropdowns: () => void = () => {
    closeFilterDropdowns();
    closeSortDropdowns();
  };

  useEffect(() => {
    if (!filterSelectionSaved) return;
    dispatch(updateAssetsFilter(selectedFilters));
  }, [selectedFilters]);

  useEffect(closeDropdowns, [selectedFilters?.length]);

  useEffect(() => {
    if (assetFilterShown) {
      setBackdropPressToHideEnabled(false);
      const timeout = setTimeout(() => {
        setBackdropPressToHideEnabled(true);
        clearTimeout(timeout);
      }, 250);
    } else setBackdropPressToHideEnabled(true);
  }, [assetFilterShown]);

  const addFilter: () => void = () => {
    setSelectedFilters([...selectedFilters, { category: null }]);
    closeDropdowns();
  };

  const onModalHide: () => void = () => {
    closeDropdowns();
    setDropdownSelection(DropdownSelection.CATEGORY);
    setSelectedFilters(appliedFilters);
  };

  const close: () => void = backdropPressToHideEnabled ? hide : () => null;

  const closeSortDropdowns: () => void = () => {
    setSortCategoryDropdownToggled(false);
    setSortOrderDropdownToggled(false);
  };

  const filters = () => selectedFilters.length > 0 && <View style={[styles.content, { zIndex: 3 }]}>
    {selectedFilters.map((item: AssetFilterSelection, index: number) => {
      const {
        category, is, value, displayValue,
      } = item;

      return <AssetFilterItem
        {...{
          category,
          is,
          index,
          value,
          displayValue,
          visibleFilterIndex,
          selectedFilters,
          setSelectedFilters,
          dropdownSelection,
          closeDropdowns,
          templates,
          setVisibleFilterIndex,
          setDropdownSelection,
          selectedLocationId,
          floorDropdownShown,
          setFloorDropdownShown,
          areaDropdownShown,
          setAreaDropdownShown,
          spaceDropdownShown,
          setSpaceDropdownShown,
          selectedFloor,
          setSelectedFloor,
          selectedArea,
          setSelectedArea,
          selectedSpace,
          setSelectedSpace,
          closeSortDropdowns,
        }}
        suggestions={categorySuggestions(category)}
        key={`filter_item_${category}`}
      />;
    })}
  </View>;

  return isMobile ? <SelectionModal
    disableSwipeToClose
    title='Filter & Sort'
    {...{ apply, onModalHide }}
    hide={() => {
      hide();
      closeDropdowns();
      setImmediate(resetOnHide);
    }}
    isVisible={assetFilterShown}
    buttonTitle='Apply'
    listContent={<View>
      <Text style={styles.title}>{'Filter'}</Text>
      {filters()}
      <PressableStyled
        style={styles.addFilterButton}
        disabled={addFilterButtonDisabled}
        onPress={addFilter}
        testID='add-filter-btn'
      >
        <PlusIcon16 />
        <Text style={styles.addFilterText}>{'Add filter'}</Text>
      </PressableStyled>
      <Text style={styles.title}>{'Sort'}</Text>
      {selectedSortCategory ? <View style={styles.content}>
        <AssetSortSelection
          {...{
            selectedSortCategory,
            setSelectedSortCategory,
            sortDescendingSelected,
            setSortDescendingSelected,
            sortCategoryDropdownToggled,
            setSortCategoryDropdownToggled,
            sortOrderDropdownToggled,
            setSortOrderDropdownToggled,
            closeFilterDropdowns,
          }}
        />
      </View> : <PressableStyled
        style={styles.addFilterButton}
        disabled={addFilterButtonDisabled}
        onPress={() => setSelectedSortCategory(AssetSortCategory.NAME)}
      >
        <PlusIcon16 />
        <Text style={styles.addFilterText}>{'Add sort'}</Text>
      </PressableStyled>}
      {assetFilterResults && <Button
        title='Reset all'
        style={styles.resetButton}
        onPress={resetAssetFilter}
      />}
    </View>}
    style={styles.modalMobile}
  /> : <Modal
    isVisible={assetFilterShown}
    onBackButtonPress={hide}
    onSwipeComplete={hide}
    onBackdropPress={close}
    style={Style.modal}
    {...{ onModalHide }}
  >
    <Pressable
      style={[Style.modalContent, styles.container]}
      onPress={closeDropdowns}
      testID='filters-modal'
    >
      <Text style={styles.title}>{'Filters'}</Text>
      {filters()}
      <PressableStyled
        style={styles.addFilterButton}
        disabled={addFilterButtonDisabled}
        onPress={addFilter}
        testID='add-filter-btn'
      >
        <Text style={styles.addFilterText}>{'Add filter'}</Text>
        <PlusIcon color={styles.addFilterText.color} />
      </PressableStyled>
      <View style={styles.buttonContainer}>
        <CheckBoxItem
          toggled={filterSelectionSaved}
          toggle={() => {
            if (filterSelectionSaved) dispatch(resetAllAssetFilters());
            else {
              dispatch(setAssetsFilterSaved(true));
              dispatch(updateAssetsFilter(selectedFilters));
            }
          }}
          title='Save filter selection '
          {...{ closeDropdowns }}
          testID='save-filter-checkbox'
        />
        <View style={Style.row}>
          <Button
            title='Cancel'
            onPress={hide}
            style={styles.button}
            testID='close-filter-modal-btn'
          />
          <Button
            affirmative
            title='Apply'
            onPress={apply}
            testID='apply-filters-btn'
            style={styles.button}
          />
        </View>
      </View>
      <ActivityIndicator
        animating={loading}
        size='large'
        style={Style.activityIndicator}
      />
    </Pressable>
  </Modal>;
};

const styles = StyleSheet.create({
  container: {
    paddingVertical: 24,
    alignSelf: 'center',
    width: 688,
    cursor: 'auto',
  },
  title: {
    fontSize: isMobile ? 18 : 22,
    lineHeight: isMobile ? 27 : 33,
    fontFamily: 'Poppins_400Regular',
    letterSpacing: 0,
    color: Colors.light.hereworksBlack600,
    borderBottomWidth: 1,
    borderBottomColor: Colors.light.hereworksWhite500,
    paddingHorizontal: isMobile ? undefined : 24,
    paddingBottom: isMobile ? undefined : 24,
    marginBottom: isMobile ? 8 : 24,
    marginTop: isMobile ? 16 : undefined,
  },
  buttonContainer: {
    ...Style.row,
    borderTopWidth: 1,
    borderTopColor: Colors.light.hereworksWhite500,
    marginTop: 24,
    paddingHorizontal: 24,
    paddingTop: 16,
  },
  button: {
    width: 126,
    height: 36,
    paddingHorizontal: 24,
  },
  content: {
    marginHorizontal: isMobile ? undefined : 24,
    marginBottom: 16,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: Colors.light.hereworksWhite300,
    paddingVertical: 6,
    paddingHorizontal: 12,
    zIndex: 2,
  },
  addFilterButton: {
    flexDirection: 'row',
    alignItems: 'center',
    marginHorizontal: isMobile ? undefined : 24,
    alignSelf: 'flex-start',
  },
  addFilterText: {
    fontSize: 14,
    lineHeight: 22,
    fontFamily: 'Roboto_400Regular',
    color: Colors.light.hereworksBlue500,
    marginStart: isMobile ? 4 : undefined,
    marginEnd: isMobile ? undefined : 4,
  },
  modalMobile: {
    maxHeight: 600,
  },
  resetButton: {
    marginTop: 24,
    width: '100%',
  },
});

export default AssetFilterModal;
